import { apiClient } from "@/services/api/axiosClient";
import type { ApiResponse } from "@/services/api/api.types";
import { API_ENDPOINTS } from "@/services/api/endpoints";

export type SupportingDocumentType =
  | "PROPERTY_WEAR_AND_TEAR"
  | "NEIGHBORHOOD_CONDITION"
  | "SYMPATHY_SAVINGS"
  | "PROPERTY_PICTURES"
  | "RENT_ROLL"
  | "INCOME_EXPENSE_STATEMENT"
  | "FUTURE_EXPENDITURES"
  | "APPRAISAL";

export const SUPPORTING_DOCUMENT_TYPES: SupportingDocumentType[] = [
  "PROPERTY_WEAR_AND_TEAR",
  "NEIGHBORHOOD_CONDITION",
  "SYMPATHY_SAVINGS",
  "PROPERTY_PICTURES",
  "RENT_ROLL",
  "INCOME_EXPENSE_STATEMENT",
  "FUTURE_EXPENDITURES",
  "APPRAISAL",
];

export type SupportingDocumentItem = {
  id: string;
  document_url?: string;
  url?: string;
  file_name?: string;
  name?: string;
  /** From GET / supporting-documents list (e.g. `file_description`). */
  file_description?: string;
  updated_at?: string;
};

export type UploadSupportingDocumentsResponse = {
  status?: string;
  message?: string;
  data?: unknown;
};

function normalizeDocumentList(payload: unknown): SupportingDocumentItem[] {
  if (Array.isArray(payload)) {
    return payload.map((raw) => normalizeDocumentItem(raw));
  }
  if (payload && typeof payload === "object") {
    const o = payload as Record<string, unknown>;
    if (Array.isArray(o.documents)) return o.documents.map((raw) => normalizeDocumentItem(raw));
    if (Array.isArray(o.items)) return o.items.map((raw) => normalizeDocumentItem(raw));
    if (Array.isArray(o.data)) return o.data.map((raw) => normalizeDocumentItem(raw));
  }
  return [];
}

function filenameFromUrl(url: string): string | undefined {
  try {
    const withoutQuery = url.split("?")[0] ?? url;
    const last = withoutQuery.split("/").filter(Boolean).pop();
    return last || undefined;
  } catch {
    return undefined;
  }
}

function normalizeDocumentItem(raw: unknown): SupportingDocumentItem {
  if (!raw || typeof raw !== "object") {
    return { id: "" };
  }

  const r = raw as Record<string, unknown>;
  const id = r.id;
  const documentUrl =
    (typeof r.document_url === "string" && r.document_url) ||
    (typeof r.url === "string" && r.url) ||
    (typeof r.document === "string" && r.document) ||
    undefined;
  const fileName =
    (typeof r.file_name === "string" && r.file_name) ||
    (typeof r.name === "string" && r.name) ||
    (documentUrl ? filenameFromUrl(documentUrl) : undefined) ||
    undefined;

  const fileDescription =
    (typeof r.file_description === "string" && r.file_description.trim()) ||
    (typeof r.description === "string" && r.description.trim()) ||
    undefined;

  const updatedAt =
    (typeof r.updated_at === "string" && r.updated_at) ||
    (typeof r.updatedAt === "string" && r.updatedAt) ||
    undefined;

  return {
    id: id == null ? "" : String(id),
    document_url: documentUrl,
    url: typeof r.url === "string" ? r.url : undefined,
    file_name: fileName,
    name: typeof r.name === "string" ? r.name : undefined,
    file_description: fileDescription,
    updated_at: updatedAt,
  };
}

export const stepsService = {
  async uploadSupportingDocuments(payload: {
    property_id: string;
    document_type: SupportingDocumentType;
    files: File[];
    file_description: string;
  }): Promise<ApiResponse<UploadSupportingDocumentsResponse>> {
    const formData = new FormData();
    formData.append("property_id", payload.property_id);
    formData.append("document_type", payload.document_type);
    formData.append("file_description", payload.file_description);
    for (const file of payload.files) {
      formData.append("files", file);
    }
    const { data } = await apiClient.post<ApiResponse<UploadSupportingDocumentsResponse>>(
      API_ENDPOINTS.PROPERTY.SUPPORTING_DOCUMENTS,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  },

  async getSupportingDocuments(
    userId: string,
    docType: SupportingDocumentType,
    propertyId: string
  ): Promise<ApiResponse<SupportingDocumentItem[]>> {
    const { data } = await apiClient.get<ApiResponse<unknown>>(
      API_ENDPOINTS.PROPERTY.SUPPORTING_DOCUMENTS,
      {
        params: {
          user_id: userId,
          document_type: docType,
          property_id: propertyId,
        },
      }
    );
    const list = normalizeDocumentList(data?.data ?? data);
    return {
      ...data,
      data: list,
    } as ApiResponse<SupportingDocumentItem[]>;
  },

  async deleteSupportingDocument(documentId: string): Promise<ApiResponse<unknown>> {
    const { data } = await apiClient.delete<ApiResponse<unknown>>(
      API_ENDPOINTS.PROPERTY.SUPPORTING_DOCUMENT_BY_ID(documentId)
    );
    return data;
  },
};
